import React from 'react';
import { Loader2, Plus, Trash2, Check } from 'lucide-react';
import { getContrastColor } from './types';
import { planningApi } from '../../lib/api';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';

/**
 * Éditeur des codes d'absence (CP, RTT, MAL...). Vit ici, dans le module Planning, parce que c'est là
 * qu'on s'en sert. Historique : ajouté aux réglages du Planning le 2026-07-23, déplacé deux jours plus
 * tard vers Paramètres (barre latérale) lors de la centralisation des réglages, puis ramené ici le
 * 2026-08-25 à la demande d'Emeni — la logique du projet est revenue, depuis le 21 août, à ranger chaque
 * réglage dans son module (RDV Pro et Pointage ont fait le même trajet).
 *
 * L'écran Paramètres le garde en secours pour les clients qui n'ont PAS le module Planning : deux d'entre
 * eux éditent ces codes sans avoir de planning (l'un n'a que Planning RDV, l'autre que les demandes de
 * congés). Sans ce repli, ils perdraient purement et simplement l'écran. Un même client ne le voit donc
 * jamais aux deux endroits.
 *
 * `onSaved` : les autres onglets des réglages Planning renvoient un payload COMBINÉ qui contient
 * absenceCodes. Sans cette remontée, modifier un code ici puis enregistrer un autre onglet réécrirait
 * l'ancienne valeur par-dessus.
 */
/**
 * `libelle`, `remunere`, `decompteConges`, `origine` et `lotId` sont arrivés avec l'import Excel
 * (docs/IMPORT-PLANNING-EXCEL.md §4) : un code rencontré dans un classeur est créé à la volée avec les
 * défauts prudents — ni rémunéré, ni décompté des congés — puis qualifié ici. Ils sont facultatifs : les
 * codes saisis à la main depuis toujours n'en portent aucun, et rien ne doit les leur imposer.
 */
export interface AbsenceCode {
    code: string;
    isFullDay: boolean;
    autoApply: boolean;
    color?: string;
    /** Le libellé tel qu'écrit dans le fichier, quand le code vient d'un import. */
    libelle?: string;
    remunere?: boolean;
    decompteConges?: boolean;
    origine?: 'import';
    lotId?: string;
}
export function AbsenceCodesSettings({ onSaved, saveRef, onSavingChange }: {
    onSaved?: (codes: AbsenceCode[]) => void;
    /**
     * Quand la modale hote fournit cette reference, elle recupere l'enregistrement pour le poser dans SON
     * pied de page, a cote de « Fermer » : le bouton interne disparait. Sans cela, l'onglet « Codes
     * d'absence » etait le seul des reglages du Planning dont le « Enregistrer » se trouvait en bas du
     * contenu, hors du pied de page ou tous les autres onglets le placent.
     */
    saveRef?: React.MutableRefObject<(() => void) | null>;
    /** Pour que le bouton de l'hote affiche « Enregistrement... » / « Enregistre » comme le bouton interne. */
    onSavingChange?: (state: { saving: boolean; saved: boolean }) => void;
}) {
    const [codes, setCodes] = React.useState<AbsenceCode[]>([]);
    const [payload, setPayload] = React.useState<any>({});
    const [newCode, setNewCode] = React.useState('');
    const [newColor, setNewColor] = React.useState('#ffe39b');
    const [loading, setLoading] = React.useState(true);
    const [saving, setSaving] = React.useState(false);
    const [saved, setSaved] = React.useState(false);

    React.useEffect(() => {
        planningApi.getSettings().then((settings: any) => {
            const raw = (settings && typeof settings === 'object') ? settings : {};
            setPayload(raw);
            const next = Array.isArray(raw.absenceCodes) && raw.absenceCodes.length > 0
                ? raw.absenceCodes.map((c: any) => (typeof c === 'string'
                    ? { code: c, isFullDay: true, autoApply: true, color: '#ffe39b' }
                    : {
                        code: String(c.code || 'ABS'),
                        isFullDay: !!c.isFullDay,
                        autoApply: c.autoApply ?? !!c.isFullDay,
                        color: c.color || '#ffe39b',
                        // Relus tels quels : un enregistrement depuis cet écran ne doit pas effacer ce que
                        // l'import a posé (d'où vient le code, et sous quel libellé).
                        libelle: c.libelle,
                        remunere: c.remunere ?? false,
                        decompteConges: c.decompteConges ?? false,
                        origine: c.origine,
                        lotId: c.lotId,
                    }))
                : [];
            setCodes(next);
        }).catch((e) => console.error('Failed to load absence codes', e)).finally(() => setLoading(false));
    }, []);

    const addCode = () => {
        const code = newCode.trim().toUpperCase();
        if (!code || codes.some((c) => c.code === code)) return;
        setCodes((prev) => [...prev, { code, isFullDay: true, autoApply: true, color: newColor, remunere: false, decompteConges: false }]);
        setNewCode('');
    };
    const removeCode = (code: string) => setCodes((prev) => prev.filter((c) => c.code !== code));
    const updateColor = (code: string, color: string) => setCodes((prev) => prev.map((c) => (c.code === code ? { ...c, color } : c)));
    const toggleFullDay = (code: string) => setCodes((prev) => prev.map((c) => (c.code === code ? { ...c, isFullDay: !c.isFullDay } : c)));
    const toggleAutoApply = (code: string) => setCodes((prev) => prev.map((c) => (c.code === code ? { ...c, autoApply: !c.autoApply } : c)));
    const toggleRemunere = (code: string) => setCodes((prev) => prev.map((c) => (c.code === code ? { ...c, remunere: !c.remunere } : c)));
    const toggleDecompte = (code: string) => setCodes((prev) => prev.map((c) => (c.code === code ? { ...c, decompteConges: !c.decompteConges } : c)));

    const handleSave = async () => {
        setSaving(true);
        try {
            await planningApi.saveSettings({ ...payload, absenceCodes: codes });
            onSaved?.(codes);
            setSaved(true);
            setTimeout(() => setSaved(false), 2000);
        } catch (e) {
            console.error('Failed to save absence codes', e);
        } finally {
            setSaving(false);
        }
    };

    // La fonction est recreee a chaque rendu (elle ferme sur `codes`), donc on la republie a chaque rendu.
    React.useEffect(() => {
        if (!saveRef) return;
        saveRef.current = handleSave;
        return () => { saveRef.current = null; };
    });
    React.useEffect(() => { onSavingChange?.({ saving, saved }); }, [saving, saved]);
    // En quittant l'onglet, on rend son bouton a l'hote dans un etat neutre : sinon un « Enregistrement... »
    // interrompu laisserait le bouton du pied de page grise pour les autres onglets.
    React.useEffect(() => () => { onSavingChange?.({ saving: false, saved: false }); }, []);

    if (loading) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-content-muted" size={24} /></div>;

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap gap-2">
                <input type="color" value={newColor} onChange={(e) => setNewColor(e.target.value)}
                    className="h-9 w-9 rounded-lg border border-border bg-surface-soft p-0.5 cursor-pointer shrink-0" />
                <input value={newCode} onChange={(e) => setNewCode(e.target.value.toUpperCase())} placeholder="Nouveau code (ex: RTT, CP...)"
                    onKeyDown={(e) => e.key === 'Enter' && addCode()}
                    className="flex-1 min-w-[160px] h-9 bg-surface border border-border rounded-[9px] px-3 text-[13px] text-content outline-none focus:border-content" />
                <button type="button" onClick={addCode} className="h-9 px-4 rounded-full bg-content text-surface text-[13px] font-semibold whitespace-nowrap shrink-0 flex items-center gap-1.5">
                    <Plus size={14} /> Ajouter
                </button>
            </div>
            <div className="rounded-xl border border-border bg-surface divide-y divide-border">
                {codes.length === 0 ? (
                    <p className="text-center text-content-muted italic text-xs py-8">Aucun code d'absence configuré.</p>
                ) : codes.map((item) => (
                    <div key={item.code} className="flex flex-wrap items-center justify-between gap-2 px-3.5 py-2.5">
                        <div className="flex flex-wrap items-center gap-3">
                            <input type="color" value={item.color || '#ffe39b'} onChange={(e) => updateColor(item.code, e.target.value)}
                                className="w-[22px] h-[22px] rounded-[6px] border-none bg-transparent cursor-pointer shrink-0" title="Changer la couleur" />
                            <span className="h-[22px] px-2 inline-flex items-center rounded-[6px] text-[11px] font-bold tracking-wider whitespace-nowrap shrink-0" style={{ backgroundColor: item.color || '#ffe39b', color: getContrastColor(item.color || '#ffe39b') }}>{item.code}</span>
                            <div className="flex gap-1.5">
                                <button onClick={() => toggleFullDay(item.code)} title="Absence journée complète"
                                    className={`h-[22px] px-2 rounded-full text-[10px] font-bold uppercase tracking-[.09em] whitespace-nowrap shrink-0 transition-colors ${item.isFullDay ? 'bg-state-info-soft text-state-info' : 'bg-surface border border-border text-content-muted hover:text-content'}`}>
                                    Journée
                                </button>
                                <button onClick={() => toggleAutoApply(item.code)} title="Appliquer automatiquement"
                                    className={`h-[22px] px-2 rounded-full text-[10px] font-bold uppercase tracking-[.09em] whitespace-nowrap shrink-0 transition-colors ${item.autoApply ? 'bg-state-ok-soft text-state-ok' : 'bg-surface border border-border text-content-muted hover:text-content'}`}>
                                    Auto
                                </button>
                                {/* Qualification d'un code (import Excel §4) : rémunéré, décompté des congés, ou
                                    simplement informatif. Les deux sont désactivés par défaut — le choix prudent. */}
                                <button onClick={() => toggleRemunere(item.code)} title="Absence rémunérée"
                                    className={`h-[22px] px-2 rounded-full text-[10px] font-bold uppercase tracking-[.09em] whitespace-nowrap shrink-0 transition-colors ${item.remunere ? 'bg-state-ok-soft text-state-ok' : 'bg-surface border border-border text-content-muted hover:text-content'}`}>
                                    Rémunéré
                                </button>
                                <button onClick={() => toggleDecompte(item.code)} title="Décompté du solde de congés"
                                    className={`h-[22px] px-2 rounded-full text-[10px] font-bold uppercase tracking-[.09em] whitespace-nowrap shrink-0 transition-colors ${item.decompteConges ? 'bg-state-info-soft text-state-info' : 'bg-surface border border-border text-content-muted hover:text-content'}`}>
                                    Congés
                                </button>
                            </div>
                            {item.origine === 'import' && (
                                <span className="h-[22px] px-2 inline-flex items-center rounded-full bg-state-warn-soft text-state-warn text-[10px] font-bold whitespace-nowrap shrink-0"
                                    title={item.libelle && item.libelle !== item.code ? `Écrit « ${item.libelle} » dans le fichier` : 'Créé automatiquement lors d’un import Excel'}>
                                    créé à l'import
                                </span>
                            )}
                        </div>
                        <button type="button" onClick={() => removeCode(item.code)} title="Retirer ce code" className="w-[30px] h-[30px] rounded-lg flex items-center justify-center text-[#8f5450] hover:bg-state-danger-soft shrink-0">
                            <Trash2 size={14} />
                        </button>
                    </div>
                ))}
            </div>
            {/* Hors modale Planning (ecran Parametres), il n'y a pas de pied de page hote : le bouton reste ici. */}
            {!saveRef && (
                <button onClick={handleSave} disabled={saving}
                    className="h-9 px-5 rounded-full bg-content text-surface text-[13px] font-semibold whitespace-nowrap shrink-0 disabled:opacity-50">
                    {saving ? 'Enregistrement...' : saved ? 'Enregistré ✓' : 'Enregistrer'}
                </button>
            )}
        </div>
    );
}
